import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { Tools } from '../core/utils/tools/index';
import { UrlParameterModel } from '../models/core/url-parameter.model';
import { ContentTypeEnum } from '../models/enums/content-type.enum';
import { ResponseTypeEnum } from '../models/enums/response-type.enum';
import { EnvironmentService } from './environment.service';

/**
 * Manages server-side communication.
 */
@Injectable({
    providedIn: 'root'
})
export class RequestService {
    private readonly http = inject(HttpClient);
    private readonly environmentService = inject(EnvironmentService);

    /**
     * Creates a HTTP request of type GET.
     * @param url URL
     * @param [id] Record's ID, if any.
     * @param [isBase] Whether or not should "/api/" be used, if the URL is already a base.
     * @param [isAbsolute] Whether the URL is absolute (full URL).
     * @param [params] URL parameters, if any.
     * @param loadRelatedData Whether related data should be requested.
     */
    makeGet<T>(
        url: string,
        id?: string,
        isBase = false,
        isAbsolute = false,
        loadRelatedData = false,
        responseType?: ResponseTypeEnum,
        ...params: UrlParameterModel[]
    ): Observable<T> {
        return this.makeRequest<T>(
            'get',
            isAbsolute ? url : Tools.Url.getUrl(url, this.environmentService.baseUrl, isBase, id),
            undefined,
            ContentTypeEnum.ApplicationJson,
            Tools.Url.getParams(params),
            undefined,
            loadRelatedData,
            responseType
        );
    }

    /**
     * Creates a HTTP request of type POST.
     * @param url URL
     * @param [data] Object to be sent in body.
     * @param [isBase] Whether or not should "/api/" be used, if the URL is already a base.
     * @param [isAbsolute] Whether the URL is absolute (full URL).
     * @param [isFileAttached] Whether there's a file attached as response.
     * @param loadRelatedData Whether related data should be requested.
     */
    makePost<T>(
        url: string,
        data?: any,
        isBase = false,
        isAbsolute = false,
        isFileAttached = false,
        loadRelatedData = false,
        responseType?: ResponseTypeEnum
    ): Observable<T> {
        return this.makeRequest<T>(
            'post',
            isAbsolute ? url : Tools.Url.getUrl(url, this.environmentService.baseUrl, isBase),
            data,
            undefined,
            undefined,
            isFileAttached,
            loadRelatedData,
            responseType
        );
    }

    /**
     * Creates a HTTP request of type PUT.
     * @param url URL
     * @param [data] Object to be sent in body.
     * @param [isBase] Whether or not should "/api/" be used, if the URL is already a base.
     * @param [isAbsolute] Whether the URL is absolute (full URL).
     * @param loadRelatedData Whether related data should be requested.
     */
    makePut<T>(
        url: string,
        data?: any,
        isBase = false,
        isAbsolute = false,
        loadRelatedData = false,
        responseType?: ResponseTypeEnum
    ): Observable<T> {
        return this.makeRequest<T>(
            'put',
            isAbsolute ? url : Tools.Url.getUrl(url, this.environmentService.baseUrl, isBase),
            data,
            undefined,
            undefined,
            undefined,
            loadRelatedData,
            responseType
        );
    }

    /**
     * Creates a HTTP request of type DELETE.
     * @param url URL
     * @param id ID of record to be removed.
     * @param [isBase] Whether or not should "/api/" be used, if the URL is already a base.
     * @param [isAbsolute] Whether the URL is absolute (full URL).
     * @param loadRelatedData Whether related data should be requested.
     */
    makeDelete<T>(
        url: string,
        id?: string,
        isBase = false,
        isAbsolute = false,
        loadRelatedData = false,
        responseType?: ResponseTypeEnum
    ): Observable<T> {
        return this.makeRequest<T>(
            'delete',
            isAbsolute ? url : Tools.Url.getUrl(url, this.environmentService.baseUrl, isBase, id),
            undefined,
            undefined,
            undefined,
            undefined,
            loadRelatedData,
            responseType
        );
    }

    /**
     * Creates a HTTP request of type DELETE.
     * @param url URL
     * @param keys Keys of the items to be removed.
     * @param [isBase] Whether or not should "/api/" be used, if the URL is already a base.
     * @param [isAbsolute] Whether the URL is absolute (full URL).
     * @param loadRelatedData Whether related data should be requested.
     */
    makeDeleteMany<T>(
        url: string,
        keys: UrlParameterModel[],
        isBase = false,
        isAbsolute = false,
        loadRelatedData = false,
        responseType?: ResponseTypeEnum
    ): Observable<T> {
        return this.makeRequest<T>(
            'delete',
            isAbsolute ? url : Tools.Url.getUrl(url, this.environmentService.baseUrl, isBase),
            undefined,
            undefined,
            Tools.Url.getParams(keys),
            undefined,
            loadRelatedData,
            responseType
        );
    }

    /**
     * Creates a HTTP request of type POST with file.
     * @param url URL
     * @param [data] File to be sent.
     * @param [isBase] Whether or not should "/api/" be used, if the URL is already a base.
     * @param [isAbsolute] Whether the URL is absolute (full URL).
     * @param loadRelatedData Whether related data should be requested.
     */
    makeFilePost<T>(
        url: string,
        data: any,
        isBase = false,
        isAbsolute = false,
        loadRelatedData = false,
        responseType?: ResponseTypeEnum
    ): Observable<T> {
        return this.makeRequest<T>(
            'post',
            isAbsolute ? url : Tools.Url.getUrl(url, this.environmentService.baseUrl, isBase),
            data,
            ContentTypeEnum.Empty,
            undefined,
            undefined,
            loadRelatedData,
            responseType
        );
    }

    /**
     * Creates a HTTP request.
     * @param type Request type.
     * @param url URL
     * @param [data] Request's body.
     * @param [contentType] Content's type.
     * @param [httpParams] URL parameters.
     * @param loadRelatedData Whether related data should be requested.
     */
    private makeRequest<T>(
        type: string,
        url: string,
        data?: any,
        contentType: ContentTypeEnum = ContentTypeEnum.ApplicationJson,
        httpParams: HttpParams = new HttpParams(),
        isFileAttached = false,
        loadRelatedData = false,
        responseType?: ResponseTypeEnum
    ): Observable<T> {
        const headerBody: any = {};

        if (contentType !== ContentTypeEnum.Empty) {
            headerBody['Content-Type'] = contentType;
        }

        if (loadRelatedData) {
            headerBody['related'] = 'true';
        }

        const headers = headerBody ? new HttpHeaders(headerBody) : new HttpHeaders();
        const httpOptions = { headers, params: httpParams };

        if (isFileAttached) {
            (httpOptions as any).responseType = 'blob';
        }

        if (!!responseType) {
            (httpOptions as any).responseType = responseType;
        }

        if (contentType === ContentTypeEnum.Empty) {
            return this.http.post<T>(url, data, httpOptions);
        } else {
            const bodyString = data ? JSON.stringify(data) : '';

            switch (type) {
                case 'post':
                    return this.http.post<T>(url, bodyString, httpOptions);
                case 'put':
                    return this.http.put<T>(url, bodyString, httpOptions);
                case 'delete':
                    return this.http.delete<T>(url, httpOptions);
                case 'get':
                default:
                    return this.http.get<T>(url, httpOptions);
            }
        }
    }
}
